"use client";

import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * «En esta página»: el índice de las secciones de la guía abierta, con seguimiento del scroll.
 *
 * 🔑 **Lee los encabezados del DOM en vez de analizar el `.mdx`.** Los artículos se compilan a
 * componentes de React, así que en el servidor no hay un árbol de encabezados que recorrer sin volver
 * a parsear el markdown — y ya hubo un intento de renderizar MDX fuera de la página que chocó con el
 * bloqueo de `react-dom/server` en las rutas de API (ver `DESARROLLO.md`). Leer `article h2[id]`
 * después de montar da exactamente lo que se ve, sin duplicar la lógica del contenido.
 *
 * ⚠️ **Depende de que `SeccionTitulo` emita `<h2 id>`.** Hasta el 2026-09-12 esos encabezados eran un
 * `<div>` en negrita y este índice habría salido vacío. Si alguien los devuelve a un `div`, esto deja
 * de funcionar **sin romperse**: simplemente no se muestra.
 *
 * ⚠️ **Que sea de cliente no le quita nada al SEO**: los encabezados en sí están en el HTML servido;
 * esto es solo navegación sobre ellos, igual que el índice de cualquier sitio de documentación.
 */

type Seccion = { id: string; texto: string };

export function EnEstaPagina() {
  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [activa, setActiva] = useState<string>("");

  useEffect(() => {
    const encabezados = [...document.querySelectorAll<HTMLHeadingElement>("article h2[id]")];
    setSecciones(encabezados.map((h) => ({ id: h.id, texto: h.innerText.trim() })));
    if (!encabezados.length) return;

    /* `rootMargin` recorta la ventana a una franja alta: así la sección activa es la que está **cerca
       del borde de arriba**, que es donde la lee quien está leyendo, y no la que ocupa más pantalla.
       Sin el recorte, al llegar al final del artículo se queda marcada la penúltima. */
    const observador = new IntersectionObserver(
      (entradas) => {
        const visibles = entradas.filter((e) => e.isIntersecting);
        if (visibles.length) setActiva(visibles[0].target.id);
      },
      { rootMargin: "-120px 0px -70% 0px", threshold: 0 },
    );
    for (const h of encabezados) observador.observe(h);
    return () => observador.disconnect();
  }, []);

  if (secciones.length < 2) return null;

  return (
    <nav aria-label="En esta página" className="rounded-2xl bg-card p-5 shadow-[var(--tarjeta-sombra)] lg:sticky lg:top-[84px] lg:self-start">
      <p className="etiqueta-seccion">En esta página</p>
      <ul className="mt-3 flex flex-col border-l">
        {secciones.map((seccion) => {
          const activo = seccion.id === activa;
          return (
            <li key={seccion.id}>
              <a
                href={`#${seccion.id}`}
                aria-current={activo ? "location" : undefined}
                className={cn(
                  "-ml-px block border-l py-1.5 pl-4 text-sm leading-snug transition-colors",
                  activo
                    ? "border-primary font-medium text-primary"
                    : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
                )}
              >
                {seccion.texto}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
